import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { handleApiError } from '../../utils/errorHandler';

const AssignClassModal = ({ child, classes, onClose, onClassAssigned }) => {
  const [childAssignedClasses, setChildAssignedClasses] = useState([]);
  const [loadingAssignedClasses, setLoadingAssignedClasses] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (child) {
      loadChildAssignedClasses(child.id);
    }
  }, [child]);

  const loadChildAssignedClasses = async (childId) => {
    try {
      setLoadingAssignedClasses(true);
      setError('');
      const response = await api.get(`/parent/children/${childId}/subjects`);
      setChildAssignedClasses(response.data);
    } catch (err) {
      console.error('Error loading assigned classes:', err);
      setError(handleApiError(err, 'Unable to load assigned classes.'));
    } finally {
      setLoadingAssignedClasses(false);
    }
  };

  const handleAssignClass = async (subjectId) => {
    if (!child) return;

    try {
      setLoadingClasses(true);
      setError('');
      await api.post(`/parent/children/${child.id}/subjects/assign`, {
        subject_id: subjectId
      });
      await loadChildAssignedClasses(child.id);
      if (onClassAssigned) {
        await onClassAssigned();
      }
    } catch (err) {
      console.error('Error assigning class:', err);
      setError(handleApiError(err, 'Unable to assign class.'));
    } finally {
      setLoadingClasses(false);
    }
  };

  const handleUnassignClass = async (subjectId) => {
    if (!child) return;

    try {
      setLoadingClasses(true);
      setError('');
      await api.post(`/parent/children/${child.id}/subjects/unassign`, {
        subject_id: subjectId
      });
      await loadChildAssignedClasses(child.id);
      if (onClassAssigned) {
        await onClassAssigned();
      }
    } catch (err) {
      console.error('Error unassigning class:', err);
      setError(handleApiError(err, 'Unable to unassign class.'));
    } finally {
      setLoadingClasses(false);
    }
  };

  if (!child) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
        maxWidth: '700px',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div className="modal-header">
          <h2>Assign Classes to {child.username}</h2>
          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        <div className="modal-body">
          {error && (
            <div className="error-message" style={{ marginBottom: '1rem', padding: '0.75rem', borderRadius: '8px' }}>
              {error}
            </div>
          )}
          {loadingAssignedClasses ? (
            <div className="loading">Loading assigned classes...</div>
          ) : (
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ marginBottom: '0.75rem', fontSize: '1rem', fontWeight: 600 }}>
                  Currently Assigned Classes:
                </h3>
                {childAssignedClasses.length > 0 ? (
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '0.5rem',
                    marginBottom: '1rem'
                  }}>
                    {childAssignedClasses.map(cls => (
                      <span
                        key={cls.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 0.75rem',
                          background: 'rgba(102, 126, 234, 0.1)',
                          color: '#667eea',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          fontWeight: 500
                        }}
                      >
                        {cls.emoji && <span>{cls.emoji}</span>}
                        {cls.name}
                        <button
                          onClick={() => handleUnassignClass(cls.id)}
                          style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            color: '#ef4444',
                            border: '1px solid #ef4444',
                            borderRadius: '4px',
                            padding: '0.25rem 0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            marginLeft: '0.25rem'
                          }}
                        >
                          Remove
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '1rem' }}>
                    No classes assigned yet.
                  </p>
                )}
              </div>

              <div>
                <h3 style={{ marginBottom: '0.75rem', fontSize: '1rem', fontWeight: 600 }}>
                  Available Classes:
                </h3>
                {loadingClasses ? (
                  <div className="loading">Loading classes...</div>
                ) : classes.length > 0 ? (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                    gap: '0.75rem' 
                  }}>
                    {classes
                      .filter(cls => !childAssignedClasses.find(ac => ac.id === cls.id))
                      .map(cls => (
                        <button
                          key={cls.id}
                          onClick={() => handleAssignClass(cls.id)}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '1rem',
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            textAlign: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.borderColor = '#667eea';
                            e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          {cls.emoji && <span style={{ fontSize: '2rem' }}>{cls.emoji}</span>}
                          <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{cls.name}</span>
                        </button>
                      ))}
                  </div>
                ) : (
                  <p style={{ color: '#718096', fontSize: '0.9rem' }}>
                    No classes available.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignClassModal;

